import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer } from "react-daisyui";
import { Route, Routes } from "react-router-dom";
import Loader from "../components/Loader";
import {
  execQuery,
  mutations,
  queries,
  subscribe,
  subscriptions,
  types,
  wrapMutation,
} from "../graphql";
import { getZoneParticipantPaging } from "../graphql/ZoneParticipantPaging";
import useAutoRetrievePagedQuery from "../hooks/useAutoRetrievingInfiniteQuery";
import { flattenInfiniteData, toInfiniteData } from "../utils/infiniteQuery";
import Header from "./Header";
import LeftSidebar from "./LeftSidebar";
import ZoneContent from "./ZoneContent";
import Participant from "./Participant";
import CreateZone from "./CreateZone";
import useCurrentUser from "../hooks/useCurrentUser";
import AddBlock from "../pages/AddBlock";

function Zone({ zoneId }: { zoneId: string }) {
  const queryClient = useQueryClient();

  const zoneQuery = useQuery(
    ["zone", zoneId],
    async () => {
      return execQuery(queries.getZone, { zoneId }).then(
        (response) => {
          if (!response.data?.getZone) {
            return null;
          }
          const zone = response.data.getZone;

          // Pre-populate participants cache
          queryClient.setQueryData(
            ["zone", zoneId, "participants"],
            toInfiniteData(zone.participants),
          );
          delete zone.participants;

          // Pre-populate block caches
          zone.blocks.forEach((block) => {
            queryClient.setQueryData(
              ["zone", zoneId, "blocks", block.blockId],
              toInfiniteData({ ...block }),
            );
            delete block.interactions;
          });

          return zone;
        },
        (error) => {
          if (error.errors[0].errorType === "ZoneNotFound") {
            console.log("Handling ZoneNotFound");
            return null;
          }
          throw error;
        },
      );
    },
    {
      enabled: !!zoneId,
      staleTime: Infinity,
    },
  );
  const participantId = zoneQuery.data?.participation?.participantId;

  const participantsQuery = useAutoRetrievePagedQuery(
    useInfiniteQuery(["zone", zoneId, "participants"], {
      queryFn: ({ pageParam: nextToken }) =>
        execQuery(getZoneParticipantPaging, {
          zoneId,
          limit: 100,
          nextToken,
        }).then((res) => res.data?.getZone?.participants),
      getNextPageParam: (lastPage) => lastPage?.nextToken,
      enabled: zoneQuery.isSuccess && !!zoneQuery.data,
      staleTime: Infinity,
    }),
  );

  const currentUser = useCurrentUser();

  useEffect(() => {
    if (zoneId) {
      const cacheKey = ["zone", zoneId, "participants"];
      const onJoinSubscription = subscribe(subscriptions.onParticipantJoin, {
        zoneId,
      }).subscribe({
        next: ({ value }) => {
          const { data } = value;
          console.log(`Participant joined`, data);
          if (data?.onParticipantJoin) {
            const joiningParticipant = data.onParticipantJoin;
            const previousState =
              queryClient.getQueryData<InfiniteData<types.ParticipantList>>(
                cacheKey,
              ) || toInfiniteData<types.ParticipantList>();
            let foundInteraction = false;
            const newState = {
              pages: previousState.pages.map((page) => ({
                ...page,
                items: page.items.map((participant) => {
                  if (
                    participant.participantId ===
                    joiningParticipant.participantId
                  ) {
                    foundInteraction = true;
                    return joiningParticipant;
                  } else {
                    return participant;
                  }
                }),
              })),
              pageParams: [...previousState.pageParams],
            };
            if (!foundInteraction) {
              newState.pages[0].items?.unshift(joiningParticipant);
            }
            queryClient.setQueryData(cacheKey, newState);
          }
        },
      });
      const onLeaveSubscription = subscribe(subscriptions.onParticipantLeave, {
        zoneId,
      }).subscribe({
        next: ({ value }) => {
          const { data } = value;
          console.log(`Participant left`, data);
          if (data?.onParticipantLeave) {
            const leavingParticipant = data.onParticipantLeave;
            const previousState =
              queryClient.getQueryData<InfiniteData<types.ParticipantList>>(
                cacheKey,
              ) || toInfiniteData<types.ParticipantList>();
            const newState = {
              pages: previousState.pages.map((page) => ({
                ...page,
                items: page.items.filter(
                  (participant) =>
                    participant.participantId !==
                    leavingParticipant.participantId,
                ),
              })),
              pageParams: [...previousState.pageParams],
            };
            queryClient.setQueryData(cacheKey, newState);
          }
        },
      });
      console.log("Subscribed to participants");
      return () => {
        console.log("Unsubscribed to participants");
        onJoinSubscription.unsubscribe();
        onLeaveSubscription.unsubscribe();
      };
    }
  }, [queryClient, zoneId]);

  const keepAliveMutation = useMutation(wrapMutation(mutations.keepAlive), {
    onSuccess: (response) => {
      console.log("Keep alive success", response.data);
      if (response.data?.keepAlive) {
        const cacheKey = ["zone", zoneId, "participants"];
        const previousParticipants =
          queryClient.getQueryData<InfiniteData<types.ParticipantList>>(
            cacheKey,
          );
        const newParticipants = {
          ...previousParticipants,
          pages: previousParticipants?.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => ({
              ...item,
              active: !!response.data?.keepAlive?.items?.find(
                (p) => p.participantId === item.participantId,
              ),
            })),
          })),
        };
        queryClient.setQueryData(cacheKey, newParticipants);
      }
    },
    onError: (error) => {
      console.log("Keep alive error", error);
    },
  });

  const keepAlive = keepAliveMutation.mutate;
  const keepAliveInterval = 60 * 1000;

  useEffect(() => {
    if (zoneId && participantId) {
      console.log("Keep alive");
      const execKeepAlive = () => {
        keepAlive({
          zoneId,
          participantId,
          ttl: keepAliveInterval * 2,
        });
      };
      execKeepAlive();
      const timer = window.setInterval(execKeepAlive, keepAliveInterval);
      return () => {
        window.clearInterval(timer);
      };
    }
  }, [keepAlive, keepAliveInterval, participantId, zoneId]);

  useEffect(() => {
    if (zoneQuery.isSuccess) {
      document.title = `Bandwagon - ${
        zoneQuery.data?.title ?? "Zone Not Found"
      }`;
    }
  }, [zoneQuery]);

  const participants = useMemo(
    () => flattenInfiniteData(participantsQuery.data, (p) => p?.items),
    [participantsQuery.data],
  ) as types.Participant[];

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((sidebarOpen) => !sidebarOpen);
  }, []);

  return (
    <>
      <Header
        currentUser={currentUser}
        zone={zoneQuery.data ?? null}
        closeSidebar={toggleSidebar}
      />
      {!zoneQuery.data ? (
        <div className="flex flex-col items-center p-10">
          {zoneQuery.isLoading ? (
            <>
              <Loader />
            </>
          ) : (
            <CreateZone />
          )}
        </div>
      ) : (
        <Drawer
          className="mt-5 lg:drawer-open"
          open={sidebarOpen}
          onClickOverlay={toggleSidebar}
          sideClassName="z-10 h-full absolute"
          side={
            <LeftSidebar
              zone={zoneQuery.data}
              participants={participants}
              closeSidebar={toggleSidebar}
            />
          }
        >
          <main className="flex-1 overflow-y-auto rounded-md bg-base-200 p-6 pt-8">
            <div className="bg-base-200">
              <div className="min-h-screen">
                <div className="flex flex-col gap-3">
                  <Routes>
                    <Route
                      path="/zone/participants/:participantId"
                      loader={({ params }) =>
                        zoneQuery.data?.participants?.items?.find(
                          (p) => p.participantId === params.participantId,
                        )
                      }
                      element={<Participant />}
                    />
                    <Route
                      path="addBlock"
                      element={<AddBlock zone={zoneQuery.data} />}
                    />
                    <Route
                      path="*"
                      element={
                        <ZoneContent
                          zone={zoneQuery.data}
                          currentUser={currentUser}
                          participants={participants}
                        />
                      }
                    />
                  </Routes>
                </div>
              </div>
            </div>
          </main>
        </Drawer>
      )}
    </>
  );
}

export default Zone;
